import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import ClientInfoDrawer from '../../CampaignDetail/drawers/ClientInfoDrawer';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { ITeamMembersResponse } from '~/types/campaigns';

const getMedicalReasonLabels = (code?: string) => {
    switch (code) {
        case 'transplant':
            return { type: 'Transplant Type', timeframe: 'Transplant Timeframe', date: 'Transplant Date' };
        case 'cat_injury':
            return { type: 'Injury Type', timeframe: 'Injury Timeframe', date: 'Accident Date' };
        case 'cat_illness':
            return { type: 'Illness Type', timeframe: 'Illness Timeframe', date: 'Illness Date' };
        default:
            return { type: 'Medical Issue Type', timeframe: 'Timeframe', date: 'Medical Issue Date' };
    }
};

type PhoneSet = {
    type: string;
    phone: string;
    id: number;
};
interface Props {
    patient?: any;
    metadata?: any;
    campaign?: any;
    application?: any;
    completionStatus?: any;
    teamMembers?: ITeamMembersResponse | null;
    onDataSaved?: () => void;
}

const ClientInfoTab: React.FC<Props> = ({
    patient,
    metadata,
    campaign,
    application,
    completionStatus,
    teamMembers,
    onDataSaved,
}) => {
    const person = patient?.person;

    const resolveStateName = (abbrev: string | undefined): string => {
        if (!abbrev) return '';
        const match = (metadata?.states || []).find(
            (s: { abbreviation: string; state: string }) =>
                s.abbreviation === abbrev || s.state === abbrev
        );
        return match?.state || abbrev;
    };
    let birthDate = person?.birth_date;
    let age: string | number = 'Unknown';
    if (birthDate) {
        birthDate = parseISO(person?.birth_date);
        const today = new Date();
        let years = today.getFullYear() - birthDate.getFullYear();
        const hasHadBirthdayThisYear =
            today.getMonth() > birthDate.getMonth() ||
            (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
        if (!hasHadBirthdayThisYear) years -= 1;
        age = years;
    }
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Use backend completion status if available, fallback to local check
    const profileSection = completionStatus?.sections?.find((s: any) => s.key === 'profile');
    const isProfileComplete = profileSection?.is_complete ?? (
        person?.gender &&
        person?.birth_date &&
        person?.marital_status &&
        patient?.ethnicity &&
        person?.zip_code &&
        person?.address_1 &&
        person?.city &&
        person?.state
    );


    // Field name to human-readable label mapping
    const fieldLabels: Record<string, string> = {
        gender: 'Gender',
        birth_date: 'Date of Birth',
        marital_status: 'Marital Status',
        ethnicity: 'Ethnicity',
        zip_code: 'ZIP Code',
        address_1: 'Street Address 1',
        city: 'City',
        state: 'State',
    };

    // Get missing fields - prefer backend data, but if empty and section is incomplete, check manually
    const getMissingFields = (): string[] => {
        // If backend provided missing fields and they're not empty, use those
        if (profileSection?.missing_fields && profileSection.missing_fields.length > 0) {
            return profileSection.missing_fields;
        }

        // If section is incomplete but backend didn't provide fields, manually check
        if (!isProfileComplete) {
            const missing: string[] = [];
            if (!person?.gender) missing.push('gender');
            if (!person?.birth_date) missing.push('birth_date');
            if (!person?.marital_status) missing.push('marital_status');
            if (!patient?.ethnicity) missing.push('ethnicity');
            if (!person?.zip_code) missing.push('zip_code');
            if (!person?.address_1) missing.push('address_1');
            if (!person?.city) missing.push('city');
            if (!person?.state) missing.push('state');
            return missing;
        }
        
        return [];
    };

    const missingFields = getMissingFields();

    return (
        <>
            <div className='mb-7.5 flex items-center justify-between'>
                <div>
                    {isProfileComplete ? (
                        <h3 className='mb-2 flex items-center text-[16px]/[24px] font-medium uppercase text-[#AFC12D]'>
                            <MdOutlineCheckCircle
                                className='mr-2'
                                size={24}
                            />
                            Complete
                        </h3>
                    ) : (
                        <h3 className='mb-2 flex items-center text-[16px]/[24px] font-medium uppercase text-[#F02D3A]'>
                            <AiOutlineExclamationCircle
                                className='mr-2'
                                size={24}
                            />
                            Incomplete
                        </h3>
                    )}
                    <h2 className='text-[22px]/[28px] font-bold'>
                        Client Info
                    </h2>
                </div>

                <button
                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                    onClick={() => {
                        setIsDrawerOpen(true);
                    }}
                >
                    Edit
                </button>
            </div>
            
            {!isProfileComplete && (
                <div className='mb-7.5 rounded-lg border border-[#F02D3A] bg-[#FEF2F2] p-4'>
                    <h3 className='mb-2 flex items-center text-[14px]/[20px] font-semibold text-[#991B1B]'>
                        <AiOutlineExclamationCircle className='mr-2' size={20} />
                        Missing Required Fields
                    </h3>
                    {missingFields.length > 0 ? (
                        <ul className='ml-6 list-disc space-y-1 text-[14px]/[20px] text-[#7F1D1D]'>
                            {missingFields.map((field: string) => (
                                <li key={field}>{fieldLabels[field] || field}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className='text-[14px]/[20px] text-[#7F1D1D]'>
                            Please complete all required fields in this section.
                        </p>
                    )}
                </div>
            )}
            <div className='mb-7.5 grid grid-cols-12 gap-7.5'>
                <div className='col-span-12 lg:col-span-6'>
                    <div className='mb-7.5 rounded-lg border border-[#E5E5E5]'>
                        <div className='p-[16px]'>
                            <h3 className='mb-4 text-[16px]/[24px] font-medium uppercase text-[#131212]'>
                                Name
                            </h3>
                            <div className='flex justify-between'>
                                <div className='grow'>
                                    <div className='mb-[16px]'>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Title
                                        </label>
                                        <div>{person?.title}</div>
                                    </div>
                                    <div
                                        className={`mb-[16px] ${
                                            person?.first_name
                                                ? ''
                                                : 'bg-[#FDEAEB] px-1'
                                        }`}
                                    >
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            First Name
                                            <span className='align-top text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        {person?.first_name ? (
                                            <div>{person?.first_name}</div>
                                        ) : (
                                            <div className='text-[#F02D3A]'>
                                                Required
                                            </div>
                                        )}
                                    </div>
                                    <div className='mb-[16px]'>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Middle Name
                                        </label>
                                        <div>{person?.middle_name}</div>
                                    </div>
                                    <div
                                        className={`mb-[16px] ${
                                            person?.last_name
                                                ? ''
                                                : 'bg-[#FDEAEB] px-1'
                                        }`}
                                    >
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Last Name
                                            <span className='align-top text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        {person?.last_name ? (
                                            <div>{person?.last_name}</div>
                                        ) : (
                                            <div className='text-[#F02D3A]'>
                                                Required
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Nickname
                                        </label>
                                        <div>{person?.nicknames}</div>
                                    </div>
                                </div>
                                <div className='grow'>
                                    <div className='mb-[16px]'>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Suffix
                                        </label>
                                        <div>{person?.suffix}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ClientInfoDrawer
                            campaign={campaign}
                            person={person}
                            patient={patient}
                            application={application}
                            isOpen={isDrawerOpen}
                            setIsOpen={setIsDrawerOpen}
                            states={metadata?.states}
                            maritalStatuses={metadata?.marital_statuses}
                            preferredLanguages={metadata?.preferred_languages}
                            ethnicities={metadata?.ethnicities}
                            onDataSaved={onDataSaved}
                        />
                    </div>

                    <div className='mb-7.5 rounded-lg border border-[#E5E5E5]'>
                        <div className='p-[16px]'>
                            <h3 className='mb-4 text-[16px]/[24px] font-medium uppercase text-[#131212]'>
                                Contact Information
                            </h3>

                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Email
                                </label>
                                {person?.email ? (
                                    <div className='break-all text-[#00AEB3]'>
                                        <Link to={`mailto:${person?.email}`}>
                                            {person?.email}
                                        </Link>
                                    </div>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                            {person?.phone_set && person.phone_set.length > 0 ? (
                                person.phone_set.map((phone: PhoneSet) => (
                                    <div key={phone.id} className='flex'>
                                        <div className='grow'>
                                            <label className='text-[12px]/[16px] text-[#949599]'>
                                                Phone Number
                                            </label>
                                            <div>{phone.phone}</div>
                                        </div>
                                        <div className='grow'>
                                            <label className='text-[12px]/[16px] text-[#949599]'>
                                                Type
                                            </label>
                                            <div>{phone?.type}</div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Phone Number
                                    </label>
                                    <div></div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='rounded-lg border border-[#E5E5E5]'>
                        <div className='p-[16px]'>
                            <h3 className='mb-4 text-[16px]/[24px] font-medium uppercase text-[#131212]'>
                                Demographic Information
                            </h3>

                            <div
                                className={`mb-[16px] ${
                                    person?.gender ? '' : 'bg-[#FDEAEB] px-1'
                                }`}
                            >
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Gender
                                    <span className='align-top text-[#F02D3A]'>
                                        *
                                    </span>
                                </label>
                                {person?.gender?.trim() ? (
                                    <div>{person.gender.trim()}</div>
                                ) : (
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                )}
                            </div>
                            <div
                                className={`mb-[16px] ${
                                    birthDate ? '' : 'bg-[#FDEAEB] px-1'
                                }`}
                            >
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Date of Birth
                                    <span className='align-top text-[#F02D3A]'>
                                        *
                                    </span>
                                </label>
                                {birthDate ? (
                                    <div>
                                        {birthDate && format(birthDate, 'PPP')}
                                    </div>
                                ) : (
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                )}
                            </div>
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Client is deceased
                                </label>
                                <div>
                                    {person?.patient_is_deceased ? 'Yes' : 'No'}
                                </div>
                            </div>
                            <div
                                className={`mb-[16px] ${
                                    person?.marital_status
                                        ? ''
                                        : 'bg-[#FDEAEB] px-1'
                                }`}
                            >
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Marital Status
                                    <span className='align-top text-[#F02D3A]'>
                                        *
                                    </span>
                                </label>
                                {person?.marital_status ? (
                                    <div>{person?.marital_status}</div>
                                ) : (
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                )}
                            </div>
                            <div
                                className={`mb-[16px] ${
                                    patient?.ethnicity
                                        ? ''
                                        : 'bg-[#FDEAEB] px-1'
                                }`}
                            >
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    With what ethnicity or race does the client
                                    identify?
                                    <span className='align-top text-[#F02D3A]'>
                                        *
                                    </span>
                                </label>
                                {patient?.ethnicity ? (
                                    <div>{patient?.ethnicity}</div>
                                ) : (
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Preferred Language
                                </label>
                                <div>{person?.preferred_language}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='col-span-12 lg:col-span-6'>
                    <div className='mb-7.5 rounded-lg border border-[#E5E5E5]'>
                        <div className='p-[16px]'>
                            <h3 className='mb-4 text-[16px]/[24px] font-medium uppercase text-[#131212]'>
                                Address
                            </h3>

                            <div
                                className={`mb-[16px] ${
                                    person?.address_1 ? '' : 'bg-[#FDEAEB] px-1'
                                }`}
                            >
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Street Address 1
                                    <span className='align-top text-[#F02D3A]'>
                                        *
                                    </span>
                                </label>
                                {person?.address_1 ? (
                                    <div>{person?.address_1}</div>
                                ) : (
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                )}
                            </div>
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Street Address 2
                                </label>
                                <div>{person?.address_2}</div>
                            </div>
                            <div
                                className={`mb-[16px] ${
                                    person?.city ? '' : 'bg-[#FDEAEB] px-1'
                                }`}
                            >
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    City
                                    <span className='align-top text-[#F02D3A]'>
                                        *
                                    </span>
                                </label>
                                {person?.city ? (
                                    <div>{person?.city}</div>
                                ) : (
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                )}
                            </div>
                            <div className='flex gap-2'>
                                <div
                                    className={`grow ${
                                        person?.state ? '' : 'bg-[#FDEAEB] px-1'
                                    }`}
                                >
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        State
                                        <span className='align-top text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    {person?.state ? (
                                        <div>{resolveStateName(person?.state)}</div>
                                    ) : (
                                        <div className='text-[#F02D3A]'>
                                            Required
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={`grow ${
                                        person?.zip_code
                                            ? ''
                                            : 'bg-[#FDEAEB] px-1'
                                    }`}
                                >
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Zip Code
                                        <span className='align-top text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    {person?.zip_code ? (
                                        <div>{person?.zip_code}</div>
                                    ) : (
                                        <div className='text-[#F02D3A]'>
                                            Required
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Primary Medical Reason */}
                    {patient?.primary_medical_issue && (
                        <div className='rounded-lg border border-[#E5E5E5]'>
                            <div className='p-[16px]'>
                                <h3 className='mb-4 text-[16px]/[24px] font-medium uppercase text-[#131212]'>
                                    Primary Medical Reason
                                </h3>
                                {(() => {
                                    const labels = getMedicalReasonLabels(patient.primary_medical_issue?.medical_reason?.code);
                                    return (
                                        <>
                                            <div className='mb-[16px]'>
                                                <label className='text-[12px]/[16px] text-[#949599]'>
                                                    Medical Reason
                                                    <span className='align-top text-[#F02D3A]'>*</span>
                                                </label>
                                                <div>{patient.primary_medical_issue?.medical_reason?.name}</div>
                                            </div>
                                            <div className='mb-[16px]'>
                                                <label className='text-[12px]/[16px] text-[#949599]'>
                                                    {labels.type}
                                                    <span className='align-top text-[#F02D3A]'>*</span>
                                                </label>
                                                <div>{patient.primary_medical_issue?.name}</div>
                                            </div>
                                            <div className='mb-[16px]'>
                                                <label className='text-[12px]/[16px] text-[#949599]'>
                                                    {labels.timeframe}
                                                    <span className='align-top text-[#F02D3A]'>*</span>
                                                </label>
                                                <div>{patient?.injury_time_frame?.name}</div>
                                            </div>
                                            <div>
                                                <label className='text-[12px]/[16px] text-[#949599]'>
                                                    {labels.date}
                                                </label>
                                                <div>
                                                    {patient?.medical_issue_date
                                                        ? format(parseISO(patient.medical_issue_date), 'M/d/yy')
                                                        : ''}
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Secondary Medical Reason */}
            {patient?.secondary_medical_issue && (
                <div className='mb-7.5 rounded-lg border border-[#E5E5E5]'>
                    <div className='p-[16px]'>
                        <h3 className='mb-4 text-[16px]/[24px] font-medium uppercase text-[#131212]'>
                            Secondary Medical Reason
                        </h3>
                        {(() => {
                            const labels = getMedicalReasonLabels(patient.secondary_medical_issue?.medical_reason?.code);
                            return (
                                <>
                                    <div className='mb-[16px]'>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Medical Reason
                                        </label>
                                        <div>{patient.secondary_medical_issue?.medical_reason?.name}</div>
                                    </div>
                                    <div className='mb-[16px]'>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            {labels.type}
                                            <span className='align-top text-[#F02D3A]'>*</span>
                                        </label>
                                        <div>{patient.secondary_medical_issue?.name}</div>
                                    </div>
                                    <div className='mb-[16px]'>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            {labels.timeframe}
                                            <span className='align-top text-[#F02D3A]'>*</span>
                                        </label>
                                        <div>{patient?.injury_time_frame?.name}</div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            {labels.date}
                                        </label>
                                        <div>
                                            {patient?.medical_issue_date
                                                ? format(parseISO(patient.medical_issue_date), 'M/d/yy')
                                                : ''}
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </>
    );
};

export default ClientInfoTab;


